import { useEffect, useState } from 'react';
import { Loader2, PartyPopper, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const MINUTE_CHOICES = [30, 45, 60, 90, 120, 180, 240, 300];
const WEEK_DAYS = [['0', 'Mon'], ['1', 'Tue'], ['2', 'Wed'], ['3', 'Thu'], ['4', 'Fri'], ['5', 'Sat'], ['6', 'Sun']];

/** The few dials that genuinely change a plan: how long a day is, which days
 * are off, and when each paper is written. */
export function SettingsTab({ exam, prefs, onPref, onPaperDates, onHolidays, saving }) {
  const [dates, setDates] = useState({});

  useEffect(() => {
    setDates(Object.fromEntries((exam?.subjects || []).map(s => [s.subject, (s.exam_date || '').slice(0, 10)])));
  }, [exam]);

  return (
    <div className="space-y-4" data-testid="planner-settings">
      <section className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="font-heading text-base md:text-lg font-bold text-slate-900">Study time</h3>
        <p className="font-body text-xs text-slate-500 mt-0.5">
          How long a normal study day is. Every third day is kept light on purpose, so a missed day has
          somewhere to land.
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <select data-testid="planner-minutes-select" value={prefs?.daily_minutes || 60}
            onChange={e => onPref('daily_minutes', Number(e.target.value))} disabled={!prefs}
            className="p-2 rounded-lg border border-slate-200 text-sm font-body bg-white">
            {[...new Set([...(prefs ? [prefs.daily_minutes] : []), ...MINUTE_CHOICES])].sort((a, b) => a - b)
              .map(m => <option key={m} value={m}>{m < 60 ? `${m} minutes` : `${Math.round(m / 6) / 10} hours`}</option>)}
          </select>
          {prefs && [
            ['mocks_enabled', 'Timed mocks'],
            ['full_portion_revision', 'Full-portion sweep'],
            ['puzzle_slots', 'Puzzle breaks'],
          ].map(([key, label]) => (
            <button key={key} data-testid={`planner-pref-${key}`} onClick={() => onPref(key, !prefs[key])}
              className={`h-9 px-3 rounded-full text-[11px] font-heading transition-colors ${prefs[key] ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
              {label}
            </button>
          ))}
          <Button size="sm" variant="outline" className="h-9 text-[11px]" data-testid="planner-holidays-btn"
            onClick={onHolidays}>
            <PartyPopper className="w-3.5 h-3.5 mr-1" /> Holidays
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {WEEK_DAYS.map(([value, label]) => {
            const off = (prefs?.weekly_off || []).includes(Number(value));
            return (
              <button key={value} data-testid={`planner-weekly-off-${label.toLowerCase()}`}
                onClick={() => onPref('weekly_off', off
                  ? (prefs.weekly_off || []).filter(d => d !== Number(value))
                  : [...(prefs?.weekly_off || []), Number(value)])}
                className={`px-2.5 py-1 rounded-full text-[11px] font-heading border transition-colors ${off ? 'bg-rose-50 border-rose-200 text-rose-600' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                {label}{off ? ' · off' : ''}
              </button>
            );
          })}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="planner-paper-dates">
        <h3 className="font-heading text-base md:text-lg font-bold text-slate-900">Exam dates</h3>
        <p className="font-body text-xs text-slate-500 mt-0.5">
          The day each paper is written. The gap between two papers then belongs to the subject whose
          paper comes next, with its revision planned in — and the evening before a paper is kept for it.
        </p>
        <div className="divide-y divide-slate-100 mt-3">
          {(exam?.subjects || []).map(s => (
            <div key={s.subject} className="flex items-center justify-between gap-3 py-2">
              <span className="font-body text-[13px] md:text-sm text-slate-800">{s.subject}</span>
              <Input type="date" value={dates[s.subject] || ''}
                min={(exam.start_date || '').slice(0, 10)} max={(exam.end_date || '').slice(0, 10)}
                data-testid={`planner-paper-date-${s.subject.replace(/\s+/g, '-').toLowerCase()}`}
                onChange={e => setDates(d => ({ ...d, [s.subject]: e.target.value }))}
                className="w-40 rounded-lg text-sm" />
            </div>
          ))}
          {!(exam?.subjects || []).length && (
            <p className="font-body text-[12px] text-slate-400 py-2">Add the subjects first under Map portion.</p>
          )}
        </div>
        {(exam?.subjects || []).length > 0 && (
          <Button size="sm" className="mt-3 bg-gradient-to-r from-indigo-500 to-violet-500 text-white"
            disabled={saving} data-testid="planner-save-dates-btn" onClick={() => onPaperDates(dates)}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save dates and replan
          </Button>
        )}
      </section>
    </div>
  );
}

export default SettingsTab;
